import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import "@/index.css";
import { AuthProvider } from "@/lib/auth";
import Protected from "@/components/Protected";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import ReferralRedirect from "@/pages/ReferralRedirect";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Contact from "@/pages/Contact";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Workouts from "@/pages/Workouts";
import Meals from "@/pages/Meals";
import FoodLog from "@/pages/FoodLog";
import CoachDashboard from "@/pages/CoachDashboard";
import Progress from "@/pages/Progress";
import Videos from "@/pages/Videos";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";
import Measurements from "@/pages/Measurements";
import Notifications from "@/pages/Notifications";
import Share from "@/pages/Share";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import ClientDetail from "@/pages/ClientDetail";
import Exercises from "@/pages/Exercises";
import Settings from "@/pages/Settings";
import StartTrial from "@/pages/StartTrial";
import InstallPrompt from "@/components/InstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

const Protect = ({ children, ...p }) => (
  <Protected {...p}>
    <Layout>{children}</Layout>
  </Protected>
);

// Detect the Google OAuth return leg SYNCHRONOUSLY during render (useEffect
// would be too late — ProtectedRoute would race the session exchange).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRoutes() {
  const location = useLocation(); // read hash from here, not window.location (not reactive)
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/r/:code" element={<ReferralRedirect />} />
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/signup" element={<Signup />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/onboarding"
        element={
          <Protected requireOnboarded={false}>
            <Onboarding />
          </Protected>
        }
      />
      <Route path="/dashboard" element={<Protect><Dashboard /></Protect>} />
      <Route path="/coach-dashboard" element={<Protect><CoachDashboard /></Protect>} />
      <Route path="/coach-dashboard/clients/:id" element={<Protect><ClientDetail /></Protect>} />
      <Route path="/workouts" element={<Protect><Workouts /></Protect>} />
      <Route path="/exercises" element={<Protect><Exercises /></Protect>} />
      <Route path="/meals" element={<Protect><Meals /></Protect>} />
      <Route path="/food" element={<Protect><FoodLog /></Protect>} />
      <Route path="/progress" element={<Protect><Progress /></Protect>} />
      <Route path="/videos" element={<Protect allowNeedsCard><Videos /></Protect>} />
      <Route path="/measurements" element={<Protect><Measurements /></Protect>} />
      <Route path="/notifications" element={<Protect><Notifications /></Protect>} />
      <Route path="/share" element={<Protect><Share /></Protect>} />
      <Route path="/settings" element={<Protect><Settings /></Protect>} />
      <Route path="/start-trial" element={<Protect allowNeedsCard requireOnboarded={false}><StartTrial /></Protect>} />
      <Route path="/pricing" element={<Protect><Pricing /></Protect>} />
      <Route path="/admin" element={<Protect adminOnly><Admin /></Protect>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" richColors />
            <InstallPrompt />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
